import { Button, TextInput } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@jugl-web/utils/consts";
import { usersApi } from "@web-src/modules/api/createApi";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useEntitiesListProvider } from "@web-src/modules/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AuthDiscardConfirmationAlert } from "../../components/AuthDiscardConfirmationAlert/AuthDiscardConfirmationAlert";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";
import { MAX_NAME_LENGTH } from "../../conts";
import { useUserAuth } from "../../providers/UserAuthProvider/useUserAuth";
import { SignUpFlowLayout } from "../SignUpFlowLayout";

type FormValues = {
  firstName: string;
  lastName: string;
  avatar?: File;
  consent: boolean;
};

interface ProfileFormProps {
  /**
   * When true, the user will not be navigated to the entry point after
   * profile creation.
   * @default false
   */
  skipNavigationAfterProfileCreation?: boolean;
}

export const ProfileForm: FC<ProfileFormProps> = ({
  skipNavigationAfterProfileCreation = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    mode: "onChange",
  });

  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState(false);

  const { me, isLoading } = useMe();
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { logout } = useUserAuth();

  const isInitialized = useRef(false);

  const [createProfile, { isLoading: isCreateLoading }] =
    usersApi.useCreateProfileMutation();

  const { refetchEntities } = useEntitiesListProvider();

  const onSubmit: SubmitHandler<FormValues> = async (params) => {
    await createProfile({
      first_name: params.firstName,
      last_name: params.lastName,
    });

    if (skipNavigationAfterProfileCreation) {
      return;
    }

    await refetchEntities();

    navigateToPage("entryPoint");
  };

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    if (me && !isLoading) {
      isInitialized.current = true;
      if (me.profile) {
        navigateToPage("entryPoint");
      }
    }
  }, [me, isLoading, navigateToPage]);

  return (
    <>
      <AuthDiscardConfirmationAlert
        isOpen={Boolean(isDiscardAlertOpen)}
        variant="profile"
        onConfirm={() => {
          logout(false);
        }}
        onClose={() => setIsDiscardAlertOpen(false)}
      />
      <SignUpFlowLayout
        type="profileForm"
        progressBar={17}
        footerClassName="pb-6"
        onBackClick={() => {
          setIsDiscardAlertOpen(true);
        }}
      >
        <SignUpFlowContentLayout
          title={t({
            id: "profile-form-page.hi-there-whats-your-name?",
            defaultMessage: "Hi there! What’s your name?",
          })}
          subtitle={t({
            id: "profile-form-page.create-account-provide-info-subtitle",
            defaultMessage: "Just a few quick details to kick things off 🎉",
          })}
          description={
            <form
              className="flex h-full w-full flex-col items-center font-secondary"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex w-full max-w-[400px] flex-col items-center justify-center gap-8">
                <TextInput
                  classNames={{
                    wrapperClassName: "w-full",
                  }}
                  autoFocus
                  label={t({
                    id: "common.first-name",
                    defaultMessage: "First Name",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  isRequired
                  {...register("firstName", {
                    required: true,
                    validate: (value) => !!value.trim(),
                    maxLength: {
                      value: MAX_NAME_LENGTH,
                      message: `Maximum character limit reached (${MAX_NAME_LENGTH})`,
                    },
                  })}
                  isInvalid={!!errors.firstName}
                  errorMessage={errors.firstName?.message}
                />
                <TextInput
                  classNames={{
                    wrapperClassName: "w-full",
                  }}
                  label={t({
                    id: "common.last-name",
                    defaultMessage: "Last Name",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  isRequired
                  {...register("lastName", {
                    required: true,
                    validate: (value) => !!value.trim(),
                    maxLength: {
                      value: MAX_NAME_LENGTH,
                      message: `Maximum character limit reached (${MAX_NAME_LENGTH})`,
                    },
                  })}
                  isInvalid={!!errors.lastName}
                  errorMessage={errors.lastName?.message}
                />
              </div>
              <div className="flex flex-col items-center pt-[32px] 2xl:pt-[64px]">
                <Button
                  className="mx-auto w-full max-w-[300px] disabled:bg-grey-200 disabled:text-dark-600 disabled:hover:bg-grey-200"
                  type="submit"
                  size="sm"
                  isDisabled={!isValid || isCreateLoading}
                >
                  {t({
                    id: "common.continue",
                    defaultMessage: "Continue",
                  })}
                </Button>
                <div className="w-full max-w-[300px] pt-5 text-center font-secondary text-sm leading-[160%] text-dark-500">
                  You are agreeing to our{" "}
                  <a
                    className="text-primary-600"
                    href={TERMS_OF_USE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    className="text-primary-600"
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            </form>
          }
        />
      </SignUpFlowLayout>
    </>
  );
};
